import { useUnit } from 'effector-react';
import { useEffect, useMemo, useRef } from 'react';
import { ChatModel, createChatModel } from '../model';

const chatModels = new Map<string, ChatModel>();

const getChatModel = (chatId: string): ChatModel => {
  if (!chatModels.has(chatId)) {
    chatModels.set(chatId, createChatModel(chatId));
  }
  return chatModels.get(chatId)!;
};

export const useChatModel = (chatId: string) => {
  const modelRef = useRef<ChatModel | null>(null);

  const model = useMemo(() => {
    if (!modelRef.current || modelRef.current !== getChatModel(chatId)) {
      modelRef.current = getChatModel(chatId);
    }
    return modelRef.current;
  }, [chatId]);

  const {
    messages,
    isLoading,
    isLoadingMore,
    hasMoreMessages,
    currentPage,
    hasNewMessages,
    error,
  } = useUnit({
    messages: model.$messages,
    isLoading: model.$isLoading,
    isLoadingMore: model.$isLoadingMore,
    hasMoreMessages: model.$hasMoreMessages,
    currentPage: model.$currentPage,
    hasNewMessages: model.$hasNewMessages,
    error: model.$error,
  });

  useEffect(() => {
    return () => {
      model.resetChat();
    };
  }, [chatId]);

  return {
    model,
    messages,
    isLoading,
    isLoadingMore,
    hasMoreMessages,
    currentPage,
    hasNewMessages,
    error,

    loadMessages: model.loadMessages,
    loadMoreMessages: model.loadMoreMessages,
    sendMessage: model.sendMessage,
    setApi: model.setApi,
    addMessage: model.addMessage,
    setMessages: model.setMessages,
    resetChat: model.resetChat,
    setPage: model.setPage,
    markNewMessagesSeen: model.markNewMessagesSeen,
  };
};

export const clearChatModel = (chatId: string) => {
  const model = chatModels.get(chatId);
  if (model) {
    model.resetChat();
    chatModels.delete(chatId);
  }
};

export const clearAllChatModels = () => {
  chatModels.forEach((model) => model.resetChat());
  chatModels.clear();
};
